/*
 * Editor store — runs the LibPDF driver CLIENT-SIDE (the "client-完結" pillar:
 * load / edit / byte-preserving save all happen in the browser; no server
 * round-trip for the core flow). The driver is browser-compatible
 * (@libpdf/core: pure-JS pako/@noble + Web Crypto + FinalizationRegistry).
 *
 * pdf.js handles page rendering separately (the driver's renderPage is a no-op
 * in JS land); this store owns document state + the Edit log + save output.
 */

import { create } from 'zustand';
import type { Edit, PdfDocument, SaveOptions, SaveResult, ShapePathSegment } from '@pdf-ticket/pdf-core';
import { createPowerPointShapePath } from '@pdf-ticket/editor';
import { effectiveRotations, type Rotation } from './effective-rotation.js';
import type { MarkupKind } from './text-markup.js';
import {
  translatePath,
  scalePath,
  remapPathToBounds,
  smoothFreehandPath,
  pathAnchorPoints,
  pathBounds,
  strokesBounds,
  translateStrokes,
  remapStrokesToBounds,
  rotateStrokes,
  FREEHAND_SHAPE_TYPE,
} from './freehand.js';
import { sortEditsForSave } from './edits-for-save.js';
import { loadCjkFallbackFont, mayNeedCjkFallback } from './cjk-font.js';
import type { PdfRect } from './search.js';

/**
 * Regenerate an add-shape's resolved PDF-space path from its prst type and a new
 * bounding box. The path is a pure function of (shapeType, bounds), so a move or
 * resize just rebuilds it — no manual point translation. Falls back to the prior
 * path if the generator doesn't know the type (shouldn't happen for placed shapes).
 */
function shapePathForBounds(
  shapeType: string,
  bounds: { x: number; y: number; width: number; height: number },
  fallback: ReadonlyArray<ShapePathSegment>,
  adjustments?: ReadonlyArray<number>,
): ReadonlyArray<ShapePathSegment> {
  const path = createPowerPointShapePath(shapeType, bounds, adjustments ? [...adjustments] : undefined);
  if (path) return path as ReadonlyArray<ShapePathSegment>;
  // Generator-less shape (freehand): the stored path IS the geometry, so move/
  // resize must remap the points onto the new bounds. Returning `fallback`
  // unchanged left the points in place while the box moved — the two cancelled
  // and the stroke appeared stuck (single-drag move/resize couldn't move it).
  return remapPathToBounds(fallback, bounds);
}

/** Translate one spatial edit by a PDF-space delta (others pass through). */
function translateOneEdit(ed: Edit, dx: number, dy: number): Edit {
  switch (ed.kind) {
    case 'add-shape': {
      const bounds = { x: ed.x + dx, y: ed.y + dy, width: ed.width, height: ed.height };
      // Freehand has no generator: translate its stored path points directly.
      const path =
        ed.shapeType === FREEHAND_SHAPE_TYPE
          ? translatePath(ed.path, dx, dy)
          : shapePathForBounds(ed.shapeType, bounds, ed.path, ed.adjustments);
      return { ...ed, x: bounds.x, y: bounds.y, path } as Edit;
    }
    case 'add-line':
      return { ...ed, x1: ed.x1 + dx, y1: ed.y1 + dy, x2: ed.x2 + dx, y2: ed.y2 + dy } as Edit;
    case 'add-image':
    case 'add-highlight':
    case 'add-annotation':
    case 'add-text':
      return { ...ed, x: ed.x + dx, y: ed.y + dy } as Edit;
    case 'add-text-markup':
      return { ...ed, rects: ed.rects.map((r) => ({ ...r, x: r.x + dx, y: r.y + dy })) } as Edit;
    case 'add-ink':
      return {
        ...ed,
        strokes: ed.strokes.map((stroke) => stroke.map((p) => ({ x: p.x + dx, y: p.y + dy }))),
      } as Edit;
    default:
      return ed;
  }
}

/**
 * Scale one spatial edit about a PDF anchor (anchor = the group's display-top-
 * left, i.e. (minX, maxY) in PDF Y-up). Position + size scale by (sx, sy); the
 * top edge of each box is held relative to the anchor.
 */
function scaleOneEdit(ed: Edit, ax: number, ay: number, sx: number, sy: number): Edit {
  const px = (x: number): number => ax + (x - ax) * sx;
  const py = (y: number): number => ay + (y - ay) * sy;
  switch (ed.kind) {
    case 'add-shape': {
      const x = px(ed.x);
      const width = Math.max(4, ed.width * sx);
      const height = Math.max(4, ed.height * sy);
      const y = py(ed.y + ed.height) - height; // hold the top edge under the anchor scale
      const bounds = { x, y, width, height };
      const path =
        ed.shapeType === FREEHAND_SHAPE_TYPE
          ? scalePath(ed.path, ax, ay, sx, sy)
          : shapePathForBounds(ed.shapeType, bounds, ed.path, ed.adjustments);
      return { ...ed, x, y, width, height, path } as Edit;
    }
    case 'add-image':
    case 'add-highlight':
    case 'add-annotation': {
      const x = px(ed.x);
      const width = Math.max(4, ed.width * sx);
      const height = Math.max(4, ed.height * sy);
      const y = py(ed.y + ed.height) - height;
      return { ...ed, x, y, width, height } as Edit;
    }
    case 'add-text': {
      const size = Math.max(4, ed.size * sy);
      const y = py(ed.y + ed.size) - size;
      return { ...ed, x: px(ed.x), y, size } as Edit;
    }
    case 'add-line':
      return { ...ed, x1: px(ed.x1), y1: py(ed.y1), x2: px(ed.x2), y2: py(ed.y2) } as Edit;
    case 'add-text-markup':
      return {
        ...ed,
        rects: ed.rects.map((r) => {
          const width = Math.max(1, r.width * sx);
          const height = Math.max(1, r.height * sy);
          return { x: px(r.x), y: py(r.y + r.height) - height, width, height };
        }),
      } as Edit;
    case 'add-ink':
      return {
        ...ed,
        strokes: ed.strokes.map((stroke) => stroke.map((p) => ({ x: px(p.x), y: py(p.y) }))),
      } as Edit;
    default:
      return ed;
  }
}

/** PDF bbox of a spatial edit (null otherwise) — for align / distribute. */
function editBoundsOf(ed: Edit): { x: number; y: number; width: number; height: number } | null {
  switch (ed.kind) {
    case 'add-shape':
    case 'add-image':
    case 'add-highlight':
    case 'add-annotation':
      return { x: ed.x, y: ed.y, width: ed.width, height: ed.height };
    case 'add-text':
      return { x: ed.x, y: ed.y, width: Math.max(4, ed.text.length * ed.size * 0.55), height: ed.size };
    case 'add-line':
      return {
        x: Math.min(ed.x1, ed.x2),
        y: Math.min(ed.y1, ed.y2),
        width: Math.abs(ed.x1 - ed.x2),
        height: Math.abs(ed.y1 - ed.y2),
      };
    case 'add-text-markup':
      return boundsOfPoints(ed.rects.flatMap((r) => [
        { x: r.x, y: r.y },
        { x: r.x + r.width, y: r.y + r.height },
      ]));
    case 'add-ink':
      return boundsOfPoints(ed.strokes.flat());
    default:
      return null;
  }
}

/** Axis-aligned bbox of a set of points, or null if empty. */
function boundsOfPoints(
  points: ReadonlyArray<{ readonly x: number; readonly y: number }>,
): { x: number; y: number; width: number; height: number } | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Kinds that can be copied / aligned (spatial, page-anchored). */
const SPATIAL_KINDS = new Set([
  'add-shape',
  'add-line',
  'add-image',
  'add-highlight',
  'add-annotation',
  'add-text',
  'add-text-markup',
  'add-ink',
]);

/** Per-paste/duplicate offset in PDF points (right + down on screen). */
const PASTE_OFFSET = 12;

const SAVE_OPTIONS: SaveOptions = { preserveFonts: true, mode: 'incremental', compress: false };

/**
 * Lazy-load the driver (+ @libpdf/core, ~1.2 MB) only when the user actually
 * opens a PDF — keeps the initial editor bundle small. Vite splits it into a
 * separate chunk; the dynamic import resolves in node tests too.
 */
async function getDriver() {
  const mod = await import('@pdf-ticket/pdf-driver-libpdf');
  return mod.libPdfDriver;
}

export type EditorStatus = 'empty' | 'loading' | 'ready' | 'saving' | 'error';

/**
 * The currently-selected placement tool. `select` is the default (clicks on
 * the page do nothing); the others convert a click on the canvas into an
 * add-* edit at the click point. add-text additionally requires the target
 * page to have a usable existing font (the driver refuses to embed
 * substitutes — font-preservation absolute rule).
 */
export type EditorTool = 'select' | 'note' | 'highlight' | 'text' | 'shape' | 'line' | 'image' | 'markup' | 'freehand';

/** An image the user picked but hasn't placed yet — armed for a page drag. */
export interface PendingImage {
  readonly bytes: Uint8Array;
  readonly naturalWidth: number;
  readonly naturalHeight: number;
  readonly name: string;
}

export type MetadataPatch = {
  readonly title?: string;
  readonly author?: string;
  readonly subject?: string;
  readonly keywords?: ReadonlyArray<string>;
};

/** A 0–1 RGB color, matching the add-shape Edit's fill/stroke contract. */
export type RgbColor = { readonly r: number; readonly g: number; readonly b: number };

/**
 * Style patch for a placed shape. `fill`/`stroke` accept a color or `null`
 * (null removes that paint — a no-fill or no-stroke shape). Omitted keys are
 * left unchanged. `opacity` of 1 clears the constant-opacity ExtGState.
 */
export interface ShapeStylePatch {
  readonly fill?: RgbColor | null;
  readonly stroke?: RgbColor | null;
  readonly strokeWidth?: number;
  readonly opacity?: number;
}

/** Transient on-page search-match highlight (cleared on close / new document). */
export interface SearchHighlight {
  readonly pageIndex: number;
  readonly rects: ReadonlyArray<PdfRect>;
}

export interface EditorState {
  readonly fileName: string | null;
  readonly originalBytes: Uint8Array | null;
  readonly doc: PdfDocument | null;
  readonly edits: ReadonlyArray<Edit>;
  /** Undo stack: prior edit-log snapshots, oldest first. */
  readonly past: ReadonlyArray<ReadonlyArray<Edit>>;
  /** Redo stack: snapshots undone but not yet superseded, next-to-redo first. */
  readonly future: ReadonlyArray<ReadonlyArray<Edit>>;
  readonly activeTool: EditorTool;
  /**
   * The currently-armed PowerPoint shape (OOXML prst id, e.g. "Rectangle",
   * "Star5"); non-null only while `activeTool === 'shape'`. The next page drag
   * places this shape. Cleared when another tool is picked or the doc resets.
   */
  readonly activeShapeType: string | null;
  /**
   * The currently-armed line tool id (e.g. "line", "lineArrow",
   * "lineDoubleArrow"); non-null only while `activeTool === 'line'`. A 2-point
   * drag then places an add-line with the matching arrowheads.
   */
  readonly activeLineTool: string | null;
  /**
   * An image picked via "画像を挿入" but not yet placed; non-null only while
   * `activeTool === 'image'`. The next page drag places it as an add-image.
   */
  readonly pendingImage: PendingImage | null;
  /**
   * The armed text-markup mode (highlight / underline / strikeout) when
   * `activeTool === 'markup'`; a page sweep marks the text runs it covers.
   */
  readonly activeMarkup: MarkupKind | null;
  /**
   * Indices (in `edits`) of the currently-selected placed edits. A single
   * selection drives the contextual style panel + per-item handles; two or more
   * enable the group bounding box + bulk move/scale/delete. Transient (not part
   * of the edit log / history); cleared when the edit set or tool changes.
   */
  readonly selectedEditIndices: ReadonlyArray<number>;
  /**
   * Original indices of pages selected in the thumbnail rail, for bulk page
   * operations (rotate). Separate from `selectedEditIndices` (placed edits);
   * transient, cleared on document reset.
   */
  readonly selectedPageIndices: ReadonlyArray<number>;
  /** Copied edits (clones), set by copySelection; pasteClipboard appends them. */
  readonly clipboard: ReadonlyArray<Edit>;
  readonly saveResult: SaveResult | null;
  readonly status: EditorStatus;
  readonly error: string | null;
  readonly searchHighlight: SearchHighlight | null;

  openFile(fileName: string, bytes: Uint8Array): Promise<void>;
  addEdit(edit: Edit): void;
  /** Append several edits as one undoable batch (e.g. text markup over runs). */
  addEdits(edits: ReadonlyArray<Edit>): void;
  /** Arm a text-markup mode (sets activeTool='markup'), or clear with null. */
  setActiveMarkup(kind: MarkupKind | null): void;
  /**
   * Remove a pending edit by its index in the log (immutable rebuild).
   * Useful when the user wants to undo a single placement without resetting
   * everything. Like `addEdit`, this invalidates any prior `saveResult` so
   * the user can't accidentally download a stale snapshot.
   */
  removeEdit(index: number): void;
  /** Move a placed edit (add-text / annotation / highlight / image) to a new PDF-space position. */
  updateEditPosition(index: number, pos: { readonly x: number; readonly y: number }): void;
  /** Replace the content of a placed edit (add-text → text; annotation/highlight → note). */
  updateEditText(index: number, value: string): void;
  /** Resize a placed edit with width/height (image / highlight / annotation), keeping its top-left display corner anchored. */
  updateEditSize(index: number, size: { readonly width: number; readonly height: number }): void;
  /**
   * Set a placed edit's FULL bounds (x, y, width, height) in one history entry.
   * Used by ↘-resize on rotated pages, where the new geometry is computed by
   * inverting the rotated screen rect (so both position and size change at once
   * and the commit matches the on-screen box).
   */
  updateEditBounds(
    index: number,
    bounds: { readonly x: number; readonly y: number; readonly width: number; readonly height: number },
  ): void;
  /** Set a placed shape's rotation (degrees, clockwise about its bbox center). */
  updateEditRotation(index: number, rotation: number): void;
  /** Set a placed shape's adjustment values (the `adj` guides) and regenerate its path. */
  updateEditAdjustments(index: number, adjustments: ReadonlyArray<number>): void;
  /** Move an add-line by a PDF-space delta (translates both endpoints). */
  moveLineBy(index: number, dx: number, dy: number): void;
  /** Move one endpoint of an add-line to a new PDF-space position. */
  updateLineEndpoint(index: number, which: 'start' | 'end', pos: { readonly x: number; readonly y: number }): void;
  /** Replace the selection with a single edit (for the style panel + outline), or null to clear. */
  selectEdit(index: number | null): void;
  /** Toggle one edit in/out of the multi-selection (Shift/Cmd-click). */
  toggleEditSelection(index: number): void;
  /** Replace the whole selection (marquee / select-all). */
  setSelection(indices: ReadonlyArray<number>): void;
  /** Remove several edits at once (bulk delete); clears the selection. */
  removeEdits(indices: ReadonlyArray<number>): void;
  /** Replace the set of selected pages (thumbnail rail), or clear with []. */
  setPageSelection(indices: ReadonlyArray<number>): void;
  /** Toggle one page in/out of the page selection (Cmd/Ctrl-click). */
  togglePageSelection(index: number): void;
  /** Clear the page selection. */
  clearPageSelection(): void;
  /**
   * Bulk-rotate the given pages by a multiple of 90° (delta, clockwise). Each
   * page's new rotation folds the delta onto its current effective rotation;
   * emitted as one undoable batch of rotate-page edits.
   */
  rotatePages(indices: ReadonlyArray<number>, deltaDeg: number): void;
  /** Translate several edits by a PDF-space delta (bulk move). */
  translateEdits(indices: ReadonlyArray<number>, dx: number, dy: number): void;
  /**
   * Scale several edits about a PDF anchor (bulk resize): each edit's position
   * and size scale by (sx, sy) relative to (anchorX, anchorY).
   */
  scaleEdits(indices: ReadonlyArray<number>, anchorX: number, anchorY: number, sx: number, sy: number): void;
  /** Copy the selected spatial edits to the clipboard. */
  copySelection(): void;
  /** Append offset clones of the clipboard and select them (no-op if empty). */
  pasteClipboard(): void;
  /** Clone the selected edits in place (offset) and select the clones (Cmd+D). */
  duplicateSelection(): void;
  /** Align the selected edits to a shared edge/center of their group bbox. */
  alignEdits(indices: ReadonlyArray<number>, mode: 'left' | 'centerX' | 'right' | 'top' | 'middleY' | 'bottom'): void;
  /** Evenly distribute ≥3 selected edits along an axis (by center). */
  distributeEdits(indices: ReadonlyArray<number>, axis: 'x' | 'y'): void;
  /** Update a placed shape's / line's fill / stroke / width / opacity (history-tracked). */
  updateEditStyle(index: number, patch: ShapeStylePatch): void;
  /**
   * Re-smooth a placed freehand stroke (Catmull-Rom through its anchor points),
   * history-tracked. No-op for non-freehand edits or strokes with < 3 anchors.
   */
  smoothEdit(index: number): void;
  /**
   * Arm a line tool for placement: sets `activeLineTool` and switches to the
   * 'line' tool. Passing null returns to 'select' and disarms.
   */
  setActiveLineTool(toolId: string | null): void;
  setActiveTool(tool: EditorTool): void;
  /**
   * Arm a PowerPoint shape for placement: sets `activeShapeType` and switches to
   * the 'shape' tool. Passing null returns to 'select' and disarms.
   */
  setActiveShape(shapeType: string | null): void;
  /** Arm a picked image for placement (next page drag drops it), or clear. */
  setPendingImage(image: PendingImage | null): void;
  /** Set or clear the transient on-page search highlight. */
  setSearchHighlight(highlight: SearchHighlight | null): void;
  /**
   * Upsert the metadata edit. Repeated edits to title/author/subject from the
   * inspector shouldn't bloat the edit log with one entry per keystroke or blur;
   * this collapses them into a single trailing `edit-metadata` whose fields are
   * the accumulated patch. If no metadata edit exists yet it's appended.
   */
  setMetadataEdit(patch: MetadataPatch): void;
  clearEdits(): void;
  /** Revert the edit log to the previous snapshot (no-op if nothing to undo). */
  undo(): void;
  /** Re-apply the most recently undone snapshot (no-op if nothing to redo). */
  redo(): void;
  save(): Promise<void>;
  reset(): void;
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/**
 * Build the state patch for a history-tracked edit-log change: push the current
 * edits onto `past`, set the new edits, and clear `future` (a fresh edit
 * invalidates the redo branch). Always nulls saveResult so a stale download
 * can't slip out.
 */
function commitEdits(s: EditorState, next: ReadonlyArray<Edit>): Partial<EditorState> {
  return { edits: next, past: [...s.past, s.edits], future: [], saveResult: null };
}

export const useEditorStore = create<EditorState>()((set, get) => ({
  fileName: null,
  originalBytes: null,
  doc: null,
  edits: [],
  past: [],
  future: [],
  activeTool: 'select',
  activeShapeType: null,
  activeLineTool: null,
  pendingImage: null,
  activeMarkup: null,
  selectedEditIndices: [],
  selectedPageIndices: [],
  clipboard: [],
  saveResult: null,
  status: 'empty',
  error: null,
  searchHighlight: null,

  async openFile(fileName, bytes) {
    // A fresh document starts with an empty edit log AND an empty history —
    // you can't undo into a different document.
    set({ status: 'loading', error: null, fileName, saveResult: null, edits: [], past: [], future: [], searchHighlight: null, activeShapeType: null, activeLineTool: null, pendingImage: null, activeMarkup: null, selectedEditIndices: [], selectedPageIndices: [], clipboard: [] });
    try {
      const driver = await getDriver();
      const doc = await driver.load(bytes);
      set({ doc, originalBytes: bytes, status: 'ready' });
    } catch (err) {
      set({ status: 'error', error: messageOf(err, 'failed to open PDF'), doc: null });
    }
  },

  addEdit(edit) {
    // Immutable append; a new edit invalidates the prior save output.
    set((s) => commitEdits(s, [...s.edits, edit]));
  },

  addEdits(edits) {
    if (edits.length === 0) return;
    set((s) => commitEdits(s, [...s.edits, ...edits]));
  },

  removeEdit(index) {
    set((s) => {
      if (index < 0 || index >= s.edits.length) return s;
      // Removing shifts indices, so the selection (an index) is no longer valid.
      return { ...commitEdits(s, s.edits.filter((_, i) => i !== index)), selectedEditIndices: [] };
    });
  },

  updateEditPosition(index, pos) {
    set((s) => {
      const e = s.edits[index];
      // Ink has no x/y bbox — it is stroke geometry. Move = translate every
      // point by the delta from its current top-left (minX, maxY) to `pos`
      // (the overlay arms itemProps with ex=minX, ey=maxY).
      if (e && e.kind === 'add-ink') {
        const b = strokesBounds(e.strokes);
        const dx = pos.x - b.x;
        const dy = pos.y - (b.y + b.height);
        if (dx === 0 && dy === 0) return s;
        const next: Edit[] = s.edits.map((ed, i) =>
          i === index && ed.kind === 'add-ink'
            ? ({ ...ed, strokes: translateStrokes(ed.strokes, dx, dy) } as Edit)
            : ed,
        );
        return commitEdits(s, next);
      }
      if (!e || !('x' in e) || !('y' in e)) return s;
      if (e.x === pos.x && e.y === pos.y) return s; // no-op move
      const next: Edit[] = s.edits.map((ed, i) => {
        if (i !== index) return ed;
        // A shape carries an absolute path; moving it rebuilds the path at the
        // new bounds (the path is a pure function of shapeType + bounds).
        if (ed.kind === 'add-shape') {
          const bounds = { x: pos.x, y: pos.y, width: ed.width, height: ed.height };
          return { ...ed, x: pos.x, y: pos.y, path: shapePathForBounds(ed.shapeType, bounds, ed.path, ed.adjustments) };
        }
        return { ...ed, x: pos.x, y: pos.y } as Edit;
      });
      return commitEdits(s, next);
    });
  },

  updateEditText(index, value) {
    set((s) => {
      const e = s.edits[index];
      if (!e) return s;
      let updated: Edit | null = null;
      if (e.kind === 'add-text') updated = { ...e, text: value };
      else if (e.kind === 'add-annotation') updated = { ...e, note: value };
      else if (e.kind === 'add-highlight') updated = { ...e, note: value || undefined };
      if (!updated) return s;
      const u = updated;
      const next = s.edits.map((ed, i) => (i === index ? u : ed));
      return commitEdits(s, next);
    });
  },

  updateEditSize(index, size) {
    set((s) => {
      const e = s.edits[index];
      if (!e || !('width' in e) || !('height' in e) || !('y' in e)) return s;
      const width = Math.max(4, size.width);
      const height = Math.max(4, size.height);
      if (e.width === width && e.height === height) return s; // no-op
      // Keep the display top-left fixed: in PDF space (x, y) is the bottom-left,
      // so the top edge is y + height — hold it constant as the box grows down.
      const y = e.y + e.height - height;
      const next: Edit[] = s.edits.map((ed, i) => {
        if (i !== index) return ed;
        // A shape's path is rebuilt to the new (anchored) bounds so the vector
        // geometry scales with the box rather than stretching a fixed path.
        if (ed.kind === 'add-shape') {
          const bounds = { x: ed.x, y, width, height };
          return { ...ed, width, height, y, path: shapePathForBounds(ed.shapeType, bounds, ed.path, ed.adjustments) };
        }
        return { ...ed, width, height, y } as Edit;
      });
      return commitEdits(s, next);
    });
  },

  updateEditBounds(index, bounds) {
    set((s) => {
      const e = s.edits[index];
      // Ink resize: remap every stroke point from its current bbox onto the
      // new bounds (4pt floor, matching shapes).
      if (e && e.kind === 'add-ink') {
        const b = { x: bounds.x, y: bounds.y, width: Math.max(4, bounds.width), height: Math.max(4, bounds.height) };
        const next: Edit[] = s.edits.map((ed, i) =>
          i === index && ed.kind === 'add-ink'
            ? ({ ...ed, strokes: remapStrokesToBounds(ed.strokes, b) } as Edit)
            : ed,
        );
        return commitEdits(s, next);
      }
      if (!e || !('x' in e) || !('y' in e) || !('width' in e) || !('height' in e)) return s;
      const width = Math.max(4, bounds.width);
      const height = Math.max(4, bounds.height);
      const { x, y } = bounds;
      if (e.x === x && e.y === y && e.width === width && e.height === height) return s; // no-op
      const next: Edit[] = s.edits.map((ed, i) => {
        if (i !== index) return ed;
        if (ed.kind === 'add-shape') {
          const b = { x, y, width, height };
          return { ...ed, x, y, width, height, path: shapePathForBounds(ed.shapeType, b, ed.path, ed.adjustments) };
        }
        return { ...ed, x, y, width, height } as Edit;
      });
      return commitEdits(s, next);
    });
  },

  updateEditRotation(index, rotation) {
    set((s) => {
      const e = s.edits[index];
      const norm = ((rotation % 360) + 360) % 360;
      // Ink has no rotation field — bake the rotation into the points about the
      // stroke bbox center (the handle's baseline is always 0 for ink).
      if (e && e.kind === 'add-ink') {
        if (norm === 0) return s;
        const b = strokesBounds(e.strokes);
        const cx = b.x + b.width / 2;
        const cy = b.y + b.height / 2;
        const next: Edit[] = s.edits.map((ed, i) =>
          i === index && ed.kind === 'add-ink'
            ? ({ ...ed, strokes: rotateStrokes(ed.strokes, cx, cy, norm) } as Edit)
            : ed,
        );
        return commitEdits(s, next);
      }
      if (!e || e.kind !== 'add-shape') return s;
      if ((e.rotation ?? 0) === norm) return s; // no-op
      // Rotation is a render/draw transform; the axis-aligned path is unchanged.
      const next = s.edits.map((ed, i) => (i === index ? ({ ...ed, rotation: norm } as Edit) : ed));
      return commitEdits(s, next);
    });
  },

  updateEditAdjustments(index, adjustments) {
    set((s) => {
      const e = s.edits[index];
      if (!e || e.kind !== 'add-shape') return s;
      const bounds = { x: e.x, y: e.y, width: e.width, height: e.height };
      const path = shapePathForBounds(e.shapeType, bounds, e.path, adjustments);
      const next = s.edits.map((ed, i) =>
        i === index ? ({ ...ed, adjustments: [...adjustments], path } as Edit) : ed,
      );
      return commitEdits(s, next);
    });
  },

  moveLineBy(index, dx, dy) {
    set((s) => {
      const e = s.edits[index];
      if (!e || e.kind !== 'add-line') return s;
      if (dx === 0 && dy === 0) return s;
      const next = s.edits.map((ed, i) =>
        i === index && ed.kind === 'add-line'
          ? ({ ...ed, x1: ed.x1 + dx, y1: ed.y1 + dy, x2: ed.x2 + dx, y2: ed.y2 + dy } as Edit)
          : ed,
      );
      return commitEdits(s, next);
    });
  },

  updateLineEndpoint(index, which, pos) {
    set((s) => {
      const e = s.edits[index];
      if (!e || e.kind !== 'add-line') return s;
      const next = s.edits.map((ed, i) => {
        if (i !== index || ed.kind !== 'add-line') return ed;
        return which === 'start'
          ? ({ ...ed, x1: pos.x, y1: pos.y } as Edit)
          : ({ ...ed, x2: pos.x, y2: pos.y } as Edit);
      });
      return commitEdits(s, next);
    });
  },

  selectEdit(index) {
    // Transient selection; never touches the edit log or history.
    set((s) => {
      const next = index !== null && index >= 0 && index < s.edits.length ? [index] : [];
      const cur = s.selectedEditIndices;
      if (cur.length === next.length && cur.every((v, i) => v === next[i])) return s;
      return { selectedEditIndices: next };
    });
  },

  toggleEditSelection(index) {
    set((s) => {
      if (index < 0 || index >= s.edits.length) return s;
      const has = s.selectedEditIndices.includes(index);
      return {
        selectedEditIndices: has
          ? s.selectedEditIndices.filter((i) => i !== index)
          : [...s.selectedEditIndices, index],
      };
    });
  },

  setSelection(indices) {
    set((s) => ({ selectedEditIndices: indices.filter((i) => i >= 0 && i < s.edits.length) }));
  },

  removeEdits(indices) {
    set((s) => {
      const drop = new Set(indices.filter((i) => i >= 0 && i < s.edits.length));
      if (drop.size === 0) return s;
      const next = s.edits.filter((_, i) => !drop.has(i));
      return { ...commitEdits(s, next), selectedEditIndices: [] };
    });
  },

  setPageSelection(indices) {
    set({ selectedPageIndices: [...new Set(indices)] });
  },

  togglePageSelection(index) {
    set((s) => ({
      selectedPageIndices: s.selectedPageIndices.includes(index)
        ? s.selectedPageIndices.filter((i) => i !== index)
        : [...s.selectedPageIndices, index],
    }));
  },

  clearPageSelection() {
    set({ selectedPageIndices: [] });
  },

  rotatePages(indices, deltaDeg) {
    set((s) => {
      if (!s.doc || indices.length === 0) return s;
      const rots = effectiveRotations(s.doc.pages, s.edits);
      const norm = (d: number): Rotation => ((((d % 360) + 360) % 360) as Rotation);
      const add: Edit[] = indices.map((i) => ({
        kind: 'rotate-page',
        page: i,
        to: norm((rots.get(i) ?? 0) + deltaDeg),
      }));
      return commitEdits(s, [...s.edits, ...add]);
    });
  },

  translateEdits(indices, dx, dy) {
    set((s) => {
      if (indices.length === 0 || (dx === 0 && dy === 0)) return s;
      const sel = new Set(indices);
      const next = s.edits.map((ed, i) => (sel.has(i) ? translateOneEdit(ed, dx, dy) : ed));
      return commitEdits(s, next);
    });
  },

  scaleEdits(indices, anchorX, anchorY, sx, sy) {
    set((s) => {
      if (indices.length === 0 || (sx === 1 && sy === 1)) return s;
      const sel = new Set(indices);
      const next = s.edits.map((ed, i) => (sel.has(i) ? scaleOneEdit(ed, anchorX, anchorY, sx, sy) : ed));
      return commitEdits(s, next);
    });
  },

  copySelection() {
    set((s) => {
      const sel = new Set(s.selectedEditIndices);
      const clip = s.edits.filter((e, i) => sel.has(i) && SPATIAL_KINDS.has(e.kind));
      return clip.length > 0 ? { clipboard: clip } : s;
    });
  },

  pasteClipboard() {
    set((s) => {
      if (s.clipboard.length === 0) return s;
      const clones = s.clipboard.map((e) => translateOneEdit(e, PASTE_OFFSET, -PASTE_OFFSET));
      const next = [...s.edits, ...clones];
      const newIndices = clones.map((_, k) => s.edits.length + k);
      // Cascade: the next paste offsets further from these clones.
      return { ...commitEdits(s, next), clipboard: clones, selectedEditIndices: newIndices };
    });
  },

  duplicateSelection() {
    set((s) => {
      const sel = new Set(s.selectedEditIndices);
      const src = s.edits.filter((e, i) => sel.has(i) && SPATIAL_KINDS.has(e.kind));
      if (src.length === 0) return s;
      const clones = src.map((e) => translateOneEdit(e, PASTE_OFFSET, -PASTE_OFFSET));
      const next = [...s.edits, ...clones];
      const newIndices = clones.map((_, k) => s.edits.length + k);
      return { ...commitEdits(s, next), selectedEditIndices: newIndices };
    });
  },

  alignEdits(indices, mode) {
    set((s) => {
      const sel = indices.filter((i) => editBoundsOf(s.edits[i] as Edit));
      if (sel.length < 2) return s;
      const bs = sel.map((i) => editBoundsOf(s.edits[i] as Edit)!);
      const minX = Math.min(...bs.map((b) => b.x));
      const maxX = Math.max(...bs.map((b) => b.x + b.width));
      const minY = Math.min(...bs.map((b) => b.y));
      const maxY = Math.max(...bs.map((b) => b.y + b.height));
      const cX = (minX + maxX) / 2;
      const cY = (minY + maxY) / 2;
      const delta = (b: { x: number; y: number; width: number; height: number }): [number, number] => {
        switch (mode) {
          case 'left':
            return [minX - b.x, 0];
          case 'right':
            return [maxX - (b.x + b.width), 0];
          case 'centerX':
            return [cX - (b.x + b.width / 2), 0];
          case 'top':
            return [0, maxY - (b.y + b.height)];
          case 'bottom':
            return [0, minY - b.y];
          case 'middleY':
            return [0, cY - (b.y + b.height / 2)];
        }
      };
      const deltas = new Map(sel.map((i, k) => [i, delta(bs[k]!)] as const));
      const next = s.edits.map((ed, i) => {
        const d = deltas.get(i);
        return d ? translateOneEdit(ed, d[0], d[1]) : ed;
      });
      return commitEdits(s, next);
    });
  },

  distributeEdits(indices, axis) {
    set((s) => {
      const sel = indices.filter((i) => editBoundsOf(s.edits[i] as Edit));
      if (sel.length < 3) return s;
      const items = sel
        .map((i) => {
          const b = editBoundsOf(s.edits[i] as Edit)!;
          return { i, c: axis === 'x' ? b.x + b.width / 2 : b.y + b.height / 2 };
        })
        .sort((a, b) => a.c - b.c);
      const first = items[0]!.c;
      const last = items[items.length - 1]!.c;
      const step = (last - first) / (items.length - 1);
      const deltas = new Map<number, [number, number]>();
      items.forEach((it, k) => {
        if (k === 0 || k === items.length - 1) return;
        const target = first + step * k;
        const d = target - it.c;
        deltas.set(it.i, axis === 'x' ? [d, 0] : [0, d]);
      });
      if (deltas.size === 0) return s;
      const next = s.edits.map((ed, i) => {
        const d = deltas.get(i);
        return d ? translateOneEdit(ed, d[0], d[1]) : ed;
      });
      return commitEdits(s, next);
    });
  },

  smoothEdit(index) {
    set((s) => {
      const e = s.edits[index];
      if (!e || e.kind !== 'add-shape' || e.shapeType !== FREEHAND_SHAPE_TYPE) return s;
      const anchors = pathAnchorPoints(e.path);
      if (anchors.length < 3) return s; // nothing to smooth
      const path = smoothFreehandPath(anchors);
      const b = pathBounds(path); // bezier overshoot may widen the bbox slightly
      const next: Edit[] = s.edits.map((ed, i) =>
        i === index ? ({ ...ed, path, x: b.x, y: b.y, width: b.width, height: b.height } as Edit) : ed,
      );
      return commitEdits(s, next);
    });
  },

  updateEditStyle(index, patch) {
    set((s) => {
      const e = s.edits[index];
      if (!e) return s;
      if (e.kind === 'add-shape') {
        const next: Edit[] = s.edits.map((ed, i) => {
          if (i !== index || ed.kind !== 'add-shape') return ed;
          return {
            ...ed,
            // null clears a paint (no fill / no stroke); a color sets it.
            ...('fill' in patch ? { fill: patch.fill ?? undefined } : {}),
            ...('stroke' in patch ? { stroke: patch.stroke ?? undefined } : {}),
            ...(patch.strokeWidth !== undefined ? { strokeWidth: patch.strokeWidth } : {}),
            // opacity 1 == fully opaque == omit the constant-opacity ExtGState.
            ...(patch.opacity !== undefined ? { opacity: patch.opacity >= 1 ? undefined : patch.opacity } : {}),
          } as Edit;
        });
        return commitEdits(s, next);
      }
      if (e.kind === 'add-line') {
        // Lines are stroked only — fill is ignored; a null stroke is kept (a
        // line must have a color).
        const next: Edit[] = s.edits.map((ed, i) => {
          if (i !== index || ed.kind !== 'add-line') return ed;
          return {
            ...ed,
            ...(patch.stroke ? { stroke: patch.stroke } : {}),
            ...(patch.strokeWidth !== undefined ? { strokeWidth: patch.strokeWidth } : {}),
            ...(patch.opacity !== undefined ? { opacity: patch.opacity >= 1 ? undefined : patch.opacity } : {}),
          } as Edit;
        });
        return commitEdits(s, next);
      }
      if (e.kind === 'add-ink') {
        // Ink (freehand /Ink) is stroked only — same shape as a line: stroke
        // color (required, never cleared), width, opacity. No fill.
        const next: Edit[] = s.edits.map((ed, i) => {
          if (i !== index || ed.kind !== 'add-ink') return ed;
          return {
            ...ed,
            ...(patch.stroke ? { stroke: patch.stroke } : {}),
            ...(patch.strokeWidth !== undefined ? { strokeWidth: patch.strokeWidth } : {}),
            ...(patch.opacity !== undefined ? { opacity: patch.opacity >= 1 ? undefined : patch.opacity } : {}),
          } as Edit;
        });
        return commitEdits(s, next);
      }
      return s;
    });
  },

  setActiveLineTool(toolId) {
    set(
      toolId
        ? { activeTool: 'line', activeLineTool: toolId, activeShapeType: null, pendingImage: null, activeMarkup: null, selectedEditIndices: [] }
        : { activeTool: 'select', activeLineTool: null },
    );
  },

  setActiveTool(tool) {
    // Switching tools disarms any pending shape/line/image/markup + clears selection.
    set(
      tool === 'shape'
        ? { activeTool: tool, activeLineTool: null, pendingImage: null, activeMarkup: null, selectedEditIndices: [] }
        : tool === 'line'
          ? { activeTool: tool, activeShapeType: null, pendingImage: null, activeMarkup: null, selectedEditIndices: [] }
          : { activeTool: tool, activeShapeType: null, activeLineTool: null, pendingImage: null, activeMarkup: null, selectedEditIndices: [] },
    );
  },

  setActiveShape(shapeType) {
    set(
      shapeType
        ? { activeTool: 'shape', activeShapeType: shapeType, activeLineTool: null, pendingImage: null, activeMarkup: null, selectedEditIndices: [] }
        : { activeTool: 'select', activeShapeType: null },
    );
  },

  setPendingImage(image) {
    set(
      image
        ? { activeTool: 'image', pendingImage: image, activeShapeType: null, activeLineTool: null, activeMarkup: null, selectedEditIndices: [] }
        : { activeTool: 'select', pendingImage: null },
    );
  },

  setActiveMarkup(kind) {
    set(
      kind
        ? { activeTool: 'markup', activeMarkup: kind, activeShapeType: null, activeLineTool: null, pendingImage: null, selectedEditIndices: [] }
        : { activeTool: 'select', activeMarkup: null },
    );
  },

  setSearchHighlight(highlight) {
    set({ searchHighlight: highlight });
  },

  setMetadataEdit(patch) {
    set((s) => {
      // Find the last edit-metadata, merge the patch into it, and move it to
      // the tail so it applies after any other edits. If none exists, append.
      const idx = [...s.edits].reverse().findIndex((e) => e.kind === 'edit-metadata');
      const prior =
        idx >= 0 ? (s.edits[s.edits.length - 1 - idx] as Extract<Edit, { kind: 'edit-metadata' }>) : null;
      const merged: Extract<Edit, { kind: 'edit-metadata' }> = {
        kind: 'edit-metadata',
        ...(prior ?? {}),
        ...patch,
      };
      const without = idx >= 0 ? s.edits.filter((_, i) => i !== s.edits.length - 1 - idx) : s.edits;
      return commitEdits(s, [...without, merged]);
    });
  },

  clearEdits() {
    set((s) => ({ ...commitEdits(s, []), selectedEditIndices: [] }));
  },

  undo() {
    set((s) => {
      if (s.past.length === 0) return s;
      const previous = s.past[s.past.length - 1];
      return {
        edits: previous,
        past: s.past.slice(0, -1),
        future: [s.edits, ...s.future],
        saveResult: null,
        selectedEditIndices: [],
      };
    });
  },

  redo() {
    set((s) => {
      if (s.future.length === 0) return s;
      const next = s.future[0];
      return {
        edits: next,
        past: [...s.past, s.edits],
        future: s.future.slice(1),
        saveResult: null,
        selectedEditIndices: [],
      };
    });
  },

  async save() {
    const { doc, edits } = get();
    if (!doc) return;
    set({ status: 'saving', error: null });
    try {
      const driver = await getDriver();
      // When add-text needs glyphs no existing page font can place (a fontless
      // page or CJK text), lazily fetch the bundled CJK fallback font so the
      // driver can embed a subset of it. The fetch is skipped for the common
      // ASCII-on-a-fonted-PDF case. Passing the font when it turns out unneeded
      // adds zero bytes (the driver only embeds edits that actually defer).
      let options = SAVE_OPTIONS;
      if (mayNeedCjkFallback(doc, edits)) {
        const cjkFallbackFont = await loadCjkFallbackFont();
        options = { ...SAVE_OPTIONS, cjkFallbackFont };
      }
      // Reorder so multiple remove-page edits apply highest-index-first;
      // see edits-for-save.ts for the rationale.
      const saveResult = await driver.save(doc, sortEditsForSave(edits), options);
      set({ saveResult, status: 'ready' });
    } catch (err) {
      set({ status: 'error', error: messageOf(err, 'failed to save PDF') });
    }
  },

  reset() {
    set({
      fileName: null,
      originalBytes: null,
      doc: null,
      edits: [],
      past: [],
      future: [],
      activeTool: 'select',
      activeShapeType: null,
      activeLineTool: null,
      pendingImage: null,
      activeMarkup: null,
      selectedEditIndices: [],
      selectedPageIndices: [],
      clipboard: [],
      saveResult: null,
      status: 'empty',
      error: null,
      searchHighlight: null,
    });
  },
}));
